import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './hooks/useAuth';
import CookieConsent from './components/CookieConsent';
import { NotificationProvider } from './hooks/useNotifications';
import LandingPage from './pages/LandingPage';
import FeaturesPage from './pages/FeaturesPage';
import ComparisonPage from './pages/ComparisonPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PreLaunchPage from './pages/PreLaunchPage';
import PreLaunchTermsPage from './pages/PreLaunchTermsPage';
import NotFoundPage from './pages/NotFoundPage';
import Dashboard from './pages/Dashboard';
import Campaigns from './pages/Campaigns';
import Suggestions from './pages/Suggestions';
import AgentActivity from './pages/AgentActivity';
import Settings from './pages/Settings';
import Insights from './pages/Insights';
import AgencyMode from './pages/AgencyMode';
import BudgetPlanner from './pages/BudgetPlanner';
import Guides from './pages/Guides';
import TestShops from './pages/TestShops';
import PoradnikiPage from './pages/PoradnikiPage';
import PublicGuide from './pages/PublicGuide';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import EbooksPage from './pages/EbooksPage';
import EbookLandingPage from './pages/EbookLandingPage';
import Layout from './components/Layout';
import AnimatedLogo from './components/AnimatedLogo';
import { ToastContainer } from './components/Toast';
import api from './utils/api';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center">
    <AnimatedLogo size={64} />
  </div>;
  return user ? children : <Navigate to="/logowanie" />;
}

// Redirect that preserves the query string (e.g. ?uid&token on reset-password).
function RedirectKeepQuery({ to }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function useTabAttention() {
  const savedTitle = useRef(document.title);
  const intervalRef = useRef(null);

  useEffect(() => {
    const messages = [
      '🤖 Twój Droid czeka...',
      '⚡ Kampanie czekają!',
      '👀 Wróć do Droida!',
      '🚀 Nie zostawiaj kampanii!',
    ];
    let idx = 0;

    const handleVisibility = () => {
      if (document.hidden) {
        savedTitle.current = document.title;
        intervalRef.current = setInterval(() => {
          document.title = messages[idx % messages.length];
          idx++;
        }, 2000);
      } else {
        if (intervalRef.current) clearInterval(intervalRef.current);
        document.title = savedTitle.current;
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);
}

function AppRoutes({ preLaunch }) {
  if (preLaunch) {
    return (
      <Routes>
        <Route path="/" element={<PreLaunchPage />} />
        <Route path="/regulamin-prelaunch" element={<PreLaunchTermsPage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/funkcje" element={<FeaturesPage />} />
      <Route path="/droid-vs-agencja" element={<ComparisonPage />} />
      <Route path="/cennik" element={<PricingPage />} />
      <Route path="/kontakt" element={<ContactPage />} />
      <Route path="/poradniki" element={<PoradnikiPage />} />
      <Route path="/poradniki/:guideId" element={<PublicGuide />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/ebooki" element={<EbooksPage />} />
      <Route path="/ebooki/:slug" element={<EbookLandingPage />} />
      <Route path="/regulamin" element={<TermsPage />} />
      <Route path="/polityka-prywatnosci" element={<PrivacyPage />} />
      <Route path="/logowanie" element={<LoginPage />} />
      <Route path="/rejestracja" element={<RegisterPage />} />
      <Route path="/odzyskaj-haslo" element={<ForgotPasswordPage />} />
      <Route path="/reset-hasla" element={<ResetPasswordPage />} />
      {/* Backward-compat redirects from old English slugs. */}
      <Route path="/login" element={<RedirectKeepQuery to="/logowanie" />} />
      <Route path="/register" element={<RedirectKeepQuery to="/rejestracja" />} />
      <Route path="/forgot-password" element={<RedirectKeepQuery to="/odzyskaj-haslo" />} />
      <Route path="/reset-password" element={<RedirectKeepQuery to="/reset-hasla" />} />
      <Route path="/app" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="suggestions" element={<Suggestions />} />
        <Route path="activity" element={<AgentActivity />} />
        <Route path="settings" element={<Settings />} />
        <Route path="insights" element={<Insights />} />
        <Route path="agency" element={<AgencyMode />} />
        <Route path="planner" element={<BudgetPlanner />} />
        <Route path="guides" element={<Guides />} />
        <Route path="guides/:guideId" element={<Guides />} />
        <Route path="test-shops" element={<TestShops />} />
        <Route path="test-shops/:shopId" element={<TestShops />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  useTabAttention();
  const [preLaunch, setPreLaunch] = useState(null);

  // Handle impersonation token from admin
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('impersonate');
    if (token) {
      localStorage.setItem('token', token);
      window.history.replaceState({}, '', window.location.pathname);
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    // ?preview=1 bypasses pre-launch mode and persists in localStorage
    const params = new URLSearchParams(window.location.search);
    if (params.get('preview') === '1') {
      localStorage.setItem('droid_preview', '1');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (localStorage.getItem('droid_preview') === '1') {
      setPreLaunch(false);
      return;
    }
    api.get('/accounts/site-config/')
      .then(res => setPreLaunch(res.data.pre_launch_mode))
      .catch(() => setPreLaunch(false));
  }, []);

  if (preLaunch === null) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <AnimatedLogo size={64} />
    </div>
  );

  return (
    <HelmetProvider>
      <AuthProvider>
        <NotificationProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppRoutes preLaunch={preLaunch} />
            <CookieConsent />
          </BrowserRouter>
          <ToastContainer />
        </NotificationProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
